import { useQuery } from "@apollo/client";
import { Navigate } from "react-router-dom";
import ErrorBoundary from "@/components/ErrorBoundary";
import {
  useCanManageJobPostsBool,
  useCanViewCareerPlanBool,
  useCanViewCareerPreferencesBool,
  useCanViewCompensationProfilesBool,
  useHasCapabilityBool,
  DOMAIN,
  PERMISSION
} from "@/hooks/useCapabilities";
import { useFlags, useFlagsLoaded } from "@/hooks/useFeatureFlags";
import { useNavigate, useParams } from "@/router";
import CareerOverview from "./_components/CareerOverview";
import {
  CAREER_OVERVIEW_OPEN_JOB_STAGES,
  canAccessCareerOverview,
  getVisibleCareerOverviewGovernanceRoles,
  mergeCareerOverviewGovernanceRoles,
  type CareerOverviewPerson
} from "./_components/careerOverviewUtils";
import {
  GET_CAREER_OVERVIEW_CAREER_PROFILE_QUERY,
  GET_CAREER_OVERVIEW_CAREER_QUERY,
  GET_CAREER_OVERVIEW_JOB_POSTS_QUERY,
  GET_CAREER_OVERVIEW_QUERY
} from "./queries";

export default function CareerOverviewPage() {
  const { pgaId } = useParams("/people/:pgaId/career/overview");
  const navigate = useNavigate();
  const canViewCareerPreferences = useCanViewCareerPreferencesBool();
  const canManageJobPosts = useCanManageJobPostsBool();
  const canViewCompensationProfiles = useCanViewCompensationProfilesBool();
  const canViewCareerPlan = useCanViewCareerPlanBool();
  const flags = useFlags();
  const flagsLoaded = useFlagsLoaded();
  const showCareerPlanning = flagsLoaded && flags["pga-career-targets"] === true && canViewCareerPlan;
  const canViewPersonCareer = useHasCapabilityBool({
    domain: DOMAIN.PEOPLE,
    permission: PERMISSION.viewPersonCareer
  });
  const canViewJobPostings = useHasCapabilityBool({
    domain: DOMAIN.JOBS,
    permission: PERMISSION.viewJobPosts
  });
  const showOverview = canAccessCareerOverview({
    canViewCareerPreferences,
    canViewCompensationProfiles,
    canManageJobPosts
  });

  const { data, loading, error } = useQuery(GET_CAREER_OVERVIEW_QUERY, {
    variables: {
      pgaId,
      includeCareerPreferences: canViewCareerPreferences,
      includeJobApplications: canManageJobPosts,
      includeCompensationProfiles: canViewCompensationProfiles
    },
    fetchPolicy: "cache-first",
    skip: !pgaId || !showOverview
  });

  const {
    data: careerData,
    loading: careerLoading,
    error: careerError
  } = useQuery(GET_CAREER_OVERVIEW_CAREER_QUERY, {
    variables: { pgaId },
    fetchPolicy: "cache-first",
    errorPolicy: "all",
    skip: !pgaId || !showOverview || !canViewPersonCareer
  });

  const {
    data: careerProfileData,
    loading: careerProfileLoading,
    error: careerProfileError
  } = useQuery(GET_CAREER_OVERVIEW_CAREER_PROFILE_QUERY, {
    variables: { pgaId: pgaId! },
    fetchPolicy: "no-cache",
    skip: !pgaId || !showOverview || !showCareerPlanning
  });

  const {
    data: jobPostsData,
    loading: jobPostsLoading,
    error: jobPostsError
  } = useQuery(GET_CAREER_OVERVIEW_JOB_POSTS_QUERY, {
    variables: {
      employerId: pgaId!,
      stages: [...CAREER_OVERVIEW_OPEN_JOB_STAGES]
    },
    fetchPolicy: "cache-first",
    skip: !pgaId || !canViewJobPostings || !showOverview
  });

  if (!showOverview && pgaId) {
    const fallbackPath = canViewPersonCareer
      ? `/people/${pgaId}/career/employment`
      : `/people/${pgaId}/person/overview`;
    return <Navigate to={fallbackPath} replace />;
  }

  if (error && !data?.person) {
    return (
      <ErrorBoundary
        variant="inline"
        title="Failed to Load Career Overview"
        message={error.message}
        className="mt-12"
      />
    );
  }

  if (jobPostsError && canViewJobPostings && !jobPostsData?.jobPosts) {
    return (
      <ErrorBoundary
        variant="inline"
        title="Failed to Load Job Postings Summary"
        message={jobPostsError.message}
        className="mt-12"
      />
    );
  }

  if (careerError && canViewPersonCareer && !careerData?.person) {
    return (
      <ErrorBoundary
        variant="inline"
        title="Failed to Load Career Details"
        message={careerError.message}
        className="mt-12"
      />
    );
  }

  if (careerProfileError && showCareerPlanning && !careerProfileData?.person) {
    return (
      <ErrorBoundary
        variant="inline"
        title="Failed to Load Career Profile"
        message={careerProfileError.message}
        className="mt-12"
      />
    );
  }

  const basePerson = data?.person;
  const person: CareerOverviewPerson = {
    pgaId: pgaId!,
    ...(basePerson ?? {}),
    ...(careerData?.person
      ? {
          employments: careerData.person.employments,
          careerInfo: careerData.person.careerInfo
        }
      : {})
  };

  return (
    <CareerOverview
      person={person}
      jobPreferences={canViewCareerPreferences ? data?.person?.jobPreferences : null}
      applications={canManageJobPosts ? data?.person?.jobApplications || [] : []}
      totalJobApplications={canManageJobPosts ? data?.person?.totalJobApplications : undefined}
      totalActiveJobApplications={canManageJobPosts ? data?.person?.totalActiveJobApplications : undefined}
      compensationProfiles={canViewCompensationProfiles ? data?.person?.compensationProfiles || [] : []}
      governanceRoles={
        canViewPersonCareer
          ? getVisibleCareerOverviewGovernanceRoles(
              mergeCareerOverviewGovernanceRoles(
                careerData?.person?.facilityGovernanceRoles || [],
                careerData?.person?.governanceRoles || []
              ),
              flags["backoffice-section-governance"] === true
            )
          : []
      }
      openJobPostCount={jobPostsData?.jobPosts?.totalCount ?? 0}
      canViewCareerPreferences={canViewCareerPreferences}
      canManageJobPosts={canManageJobPosts}
      canViewCompensationProfiles={canViewCompensationProfiles}
      canViewCareerPlan={showCareerPlanning}
      careerProfile={showCareerPlanning ? careerProfileData?.person : undefined}
      careerProfileLoading={showCareerPlanning && careerProfileLoading}
      canViewJobPostings={canViewJobPostings}
      canViewPersonCareer={canViewPersonCareer}
      loading={loading || (canViewPersonCareer && careerLoading) || (canViewJobPostings && jobPostsLoading)}
      navigate={navigate}
    />
  );
}
